import LegalPageLayout from '../../components/LegalPageLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy | EvidentTrail"
      description="EvidentTrail Privacy Policy. Learn how we collect, use, and protect your data in our read-only compliance and audit evidence service."
    >
      <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Privacy Policy (Early Access)</h1>
      <p className="text-slate-400 mb-8">Effective date: January 2026</p>

      <p className="text-slate-300 leading-relaxed mb-6">
        EvidentTrail ("we", "us") provides a read-only compliance and audit evidence service for software 
        change control. This Privacy Policy explains what data we collect, how we use it, and your choices. 
        This is an Early Access service and may evolve; we will update this policy if our practices change.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">What we collect</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li><strong className="text-white">Account data:</strong> email address, password hash (if applicable), and authentication tokens.</li>
        <li><strong className="text-white">Organization data:</strong> tenant/company name, member roles, and configuration settings.</li>
        <li><strong className="text-white">Connected system data (GitHub):</strong> repository metadata and activity needed to generate audit evidence (for example pull request events, review actions, commit metadata, branch protection/ruleset settings, workflow run results when enabled by your plan).</li>
        <li><strong className="text-white">Usage and diagnostics:</strong> service logs, timestamps, and error diagnostics for reliability and security.</li>
        <li><strong className="text-white">Payment data (if applicable):</strong> subscription status and billing identifiers from Stripe. We do not store full card numbers; payments are handled by Stripe.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">How we use data</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li><strong className="text-white">Provide the service:</strong> generate audit timelines, control evaluations, drift detection, and exception tracking.</li>
        <li><strong className="text-white">Security:</strong> protect accounts, verify webhook signatures, prevent abuse, and maintain audit logging of administrative actions.</li>
        <li><strong className="text-white">Reliability:</strong> troubleshoot errors and improve stability and performance.</li>
        <li><strong className="text-white">Billing (if applicable):</strong> manage subscriptions, enforce plan entitlements, and handle invoices.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Legal bases (EU/EEA)</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li><strong className="text-white">Contract:</strong> to provide the service you request.</li>
        <li><strong className="text-white">Legitimate interests:</strong> to secure, operate, and improve the service.</li>
        <li><strong className="text-white">Legal obligation:</strong> where required by law.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Data sharing</h2>
      <p className="text-slate-300 leading-relaxed mb-4">We share data only with:</p>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>Infrastructure providers required to operate the service (hosting, database, monitoring).</li>
        <li>Stripe for billing (if you subscribe).</li>
      </ul>
      <p className="text-slate-300 leading-relaxed mt-4">We do not sell personal data.</p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Data retention</h2>
      <p className="text-slate-300 leading-relaxed">
        We retain data according to your plan's retention settings where applicable. We may keep limited 
        security logs longer when necessary to protect the service and investigate incidents.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">International transfers</h2>
      <p className="text-slate-300 leading-relaxed">
        Our service providers may process data in regions outside your country. We use reputable providers 
        and appropriate safeguards where required.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Your rights</h2>
      <p className="text-slate-300 leading-relaxed">
        Depending on your location, you may have rights to access, correct, export, or delete your data.{' '}
        <a href="/#contact" className="text-primary-400 hover:text-primary-300">
          Contact us
        </a>{' '}to make a request.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Security</h2>
      <p className="text-slate-300 leading-relaxed">
        We implement reasonable technical and organizational measures, including tenant isolation, access 
        controls, and audit logging. No method of transmission or storage is 100% secure.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Contact</h2>
      <p className="text-slate-300 leading-relaxed">
        Questions or requests?{' '}
        <a href="/#contact" className="text-primary-400 hover:text-primary-300">
          Get in touch
        </a>{' '}through our contact form.
      </p>
    </LegalPageLayout>
  );
}
